import { inject, Injectable } from '@angular/core';
import { SupabaseService } from './supabase.service';
import {
  BodyScanDetail,
  BodyScanSummary,
  IntakeQuestion,
  IntakeSessionDetail,
  IntakeSessionListItem,
  MealHistoryRow,
  PlanDetail,
  PlanListItem,
  UsageLedgerRow,
  UsageSummary,
} from '../models/diets.models';

/**
 * Read-only RPCs for the diet feature. All RPCs live in the `diets` schema;
 * we scope the client once via a private accessor. Every call is club-scoped
 * through `SupabaseService.clubId`.
 *
 * Note on casts: the Supabase-generated types return `Json` for jsonb columns
 * (`label_i18n`, `options`) and for RPCs that return `jsonb` directly
 * (`get_plan`, `get_intake_session`, `get_latest_body_scan`, `get_usage_summary`).
 * We narrow via `as unknown as X` — the database is the source of truth for
 * the shape; the client-side models just describe what we already know.
 */
@Injectable({ providedIn: 'root' })
export class DietQueriesService {
  private readonly supabaseService = inject(SupabaseService);

  private get dietsSchema() {
    return this.supabaseService.supabase.schema('diets');
  }

  private get clubId(): string {
    return this.supabaseService.clubId;
  }

  async getIntakeQuestions(): Promise<IntakeQuestion[]> {
    const { data, error } = await this.dietsSchema.rpc(
      'get_intake_questions',
      { p_club_id: this.clubId }
    );
    if (error) {
      console.error('diets.get_intake_questions:', error);
      return [];
    }
    return (data ?? []) as unknown as IntakeQuestion[];
  }

  async listIntakeSessions(userId?: string): Promise<IntakeSessionListItem[]> {
    const args: { p_club_id: string; p_user_id?: string } = { p_club_id: this.clubId };
    if (userId) args.p_user_id = userId;
    const { data, error } = await this.dietsSchema.rpc(
      'list_intake_sessions',
      args
    );
    if (error) {
      console.error('diets.list_intake_sessions:', error);
      return [];
    }
    return (data ?? []) as unknown as IntakeSessionListItem[];
  }

  async getIntakeSession(sessionId: string): Promise<IntakeSessionDetail | null> {
    const { data, error } = await this.dietsSchema.rpc('get_intake_session', {
      p_session_id: sessionId,
    });
    if (error) {
      console.error('diets.get_intake_session:', error);
      return null;
    }
    return (data as unknown as IntakeSessionDetail) ?? null;
  }

  async listPlans(userId?: string): Promise<PlanListItem[]> {
    const args: { p_club_id: string; p_user_id?: string } = { p_club_id: this.clubId };
    if (userId) args.p_user_id = userId;
    const { data, error } = await this.dietsSchema.rpc('list_plans', args);
    if (error) {
      console.error('diets.list_plans:', error);
      return [];
    }
    return (data ?? []) as unknown as PlanListItem[];
  }

  async getPlan(planId: string): Promise<PlanDetail | null> {
    const { data, error } = await this.dietsSchema.rpc('get_plan', {
      p_plan_id: planId,
    });
    if (error) {
      console.error('diets.get_plan:', error);
      return null;
    }
    return (data as unknown as PlanDetail) ?? null;
  }

  async listBodyScans(
    userId: string,
    limit = 50
  ): Promise<BodyScanSummary[]> {
    const { data, error } = await this.dietsSchema.rpc('list_body_scans', {
      p_club_id: this.clubId,
      p_user_id: userId,
      p_limit: limit,
    });
    if (error) {
      console.error('diets.list_body_scans:', error);
      return [];
    }
    return (data ?? []) as unknown as BodyScanSummary[];
  }

  async getLatestBodyScan(userId: string): Promise<BodyScanDetail | null> {
    const { data, error } = await this.dietsSchema.rpc(
      'get_latest_body_scan',
      { p_club_id: this.clubId, p_user_id: userId }
    );
    if (error) {
      console.error('diets.get_latest_body_scan:', error);
      return null;
    }
    return (data as unknown as BodyScanDetail) ?? null;
  }

  async getUsageSummary(from: string, to: string): Promise<UsageSummary | null> {
    const { data, error } = await this.dietsSchema.rpc('get_usage_summary', {
      p_club_id: this.clubId,
      p_from: from,
      p_to: to,
    });
    if (error) {
      console.error('diets.get_usage_summary:', error);
      return null;
    }
    return (data as unknown as UsageSummary) ?? null;
  }

  /**
   * Member-facing. Returns the caller's own most recent approved plan
   * (via auth.uid() on the server — callers cannot see someone else's plan).
   * The `as never` cast is because supabase.types.ts hasn't been regenerated
   * yet with this RPC; once that happens, the cast can be removed.
   */
  async getMyActivePlan(): Promise<PlanDetail | null> {
    const { data, error } = await this.dietsSchema.rpc(
      'get_my_active_plan' as never,
      { p_club_id: this.clubId } as never
    );
    if (error) {
      console.error('diets.get_my_active_plan:', error);
      return null;
    }
    return (data as unknown as PlanDetail) ?? null;
  }

  /**
   * Member-facing body scan history. Server filters by auth.uid() so callers
   * only ever see their own measurements. Same shape as listBodyScans —
   * deltas/charts on /my-diet-plan reuse BodyScanSummary directly.
   */
  async listMyBodyScans(limit = 24): Promise<BodyScanSummary[]> {
    const { data, error } = await this.dietsSchema.rpc(
      'list_my_body_scans' as never,
      { p_club_id: this.clubId, p_limit: limit } as never
    );
    if (error) {
      console.error('diets.list_my_body_scans:', error);
      return [];
    }
    return (data ?? []) as unknown as BodyScanSummary[];
  }

  // ---- Meal history (adherence) -----------------------------------------

  /** Admin: history of meals a member actually ate, in this club. */
  async listMealHistory(
    userId: string,
    from?: string,
    to?: string,
    limit = 200
  ): Promise<MealHistoryRow[]> {
    const args: Record<string, unknown> = {
      p_club_id: this.clubId,
      p_user_id: userId,
      p_limit: limit,
    };
    if (from) args['p_from'] = from;
    if (to) args['p_to'] = to;
    const { data, error } = await this.dietsSchema.rpc(
      'list_meal_history' as never,
      args as never
    );
    if (error) {
      console.error('diets.list_meal_history:', error);
      return [];
    }
    return (data ?? []) as unknown as MealHistoryRow[];
  }

  /** Member-facing: caller's own history (uses auth.uid() server-side). */
  async listMyMealHistory(
    from?: string,
    to?: string,
    limit = 200
  ): Promise<MealHistoryRow[]> {
    const args: Record<string, unknown> = {
      p_club_id: this.clubId,
      p_limit: limit,
    };
    if (from) args['p_from'] = from;
    if (to) args['p_to'] = to;
    const { data, error } = await this.dietsSchema.rpc(
      'list_my_meal_history' as never,
      args as never
    );
    if (error) {
      console.error('diets.list_my_meal_history:', error);
      return [];
    }
    return (data ?? []) as unknown as MealHistoryRow[];
  }

  // ---- Body-scan report file -------------------------------------------

  /** Returns a 24h-signed URL for the raw scan upload, or null if missing. */
  async getBodyScanFileUrl(scanId: string): Promise<string | null> {
    const { data: pathData, error: pathErr } = await this.dietsSchema.rpc(
      'get_body_scan_file_path' as never,
      { p_scan_id: scanId } as never
    );
    if (pathErr || !pathData) {
      if (pathErr) console.error('diets.get_body_scan_file_path:', pathErr);
      return null;
    }
    const path = pathData as unknown as string;
    const { data: signed, error: signedErr } = await this.supabaseService.supabase
      .storage.from('bodycomp')
      .createSignedUrl(path, 24 * 60 * 60);
    if (signedErr || !signed?.signedUrl) {
      console.error('createSignedUrl:', signedErr);
      return null;
    }
    return signed.signedUrl;
  }

  async getUsageLedger(
    from: string,
    to: string,
    limit = 500
  ): Promise<UsageLedgerRow[]> {
    const { data, error } = await this.dietsSchema.rpc('get_usage_ledger', {
      p_club_id: this.clubId,
      p_from: from,
      p_to: to,
      p_limit: limit,
    });
    if (error) {
      console.error('diets.get_usage_ledger:', error);
      return [];
    }
    return (data ?? []) as unknown as UsageLedgerRow[];
  }
}
